import { IHolding } from "../holding"
import { HoldingUserRole } from "../permissions/holding/api"
import { AccessPermission, ReadWritePermissions, UserPermissions } from "../permissions/types"
import { IOmniaShell } from "../shell"
import { IOmniaSession } from "../types"
import { IOmniaUser, RoleId } from "../user"
import { isFunction, isNumber, isObject, isString } from "../utils/utils"

const internalRoles = new Set([
    RoleId.Manager,
    RoleId.Agronomist,
    RoleId.InvoicingAdmin,
    RoleId.ServiceSpecialist,
    RoleId.SoilMappingTeam,
    RoleId.Admin,
    RoleId.Master
])

const adminRoles = new Set([
    RoleId.Admin,
    RoleId.Master
])

type RoleLikeArg = RoleId | IOmniaShell | IOmniaSession | IOmniaUser

type SessionLikeArg = IOmniaShell | IOmniaSession

export function isInternalRole(arg: RoleLikeArg) {
    return internalRoles.has(getRole(arg))
}

export function isExternalRole(arg: RoleLikeArg) {
    return !isInternalRole(arg)
}

export function isAdmin(arg: RoleLikeArg) {
    return adminRoles.has(getRole(arg))
}

export function hasPermission(arg: IOmniaShell | IOmniaSession, selector: (p: UserPermissions) => boolean) {
    const session = getSession(arg)
    return selector(session.permissions)
}

export function getUserRole(user: IOmniaUser) {
    return (user ? user.Role : 0) || RoleId.Demo
}

export function isMaster(arg: RoleLikeArg) {
    return getRole(arg) === RoleId.Master
}

export function isHoldingOwner(arg: SessionLikeArg) {
    return getSession(arg)?.currentHoldingUserRole === HoldingUserRole.Owner
}

export function isRestrictedMember(arg: SessionLikeArg) {
    return getSession(arg)?.currentHoldingUserRole === HoldingUserRole.RestrictedMember
}

export function isAgronomist(arg: IOmniaShell | IOmniaSession | RoleId) {
    return getRole(arg) <= RoleId.Agronomist
}

export function hasAccessPermission(arg: boolean | AccessPermission | ReadWritePermissions) {

    if (isObject(arg)) {
        return Boolean(arg["read"] ?? arg["enabled"])
    }

    return Boolean(arg)

}

function getRole(arg: RoleLikeArg) {

    const role = isNumber(arg)
        ? arg
        : isUser(arg)
            ? arg.Role
            : getSession(arg)?.currentUserRole

    return role

}

function isUser(arg: {}): arg is IOmniaUser {
    return isObject(arg) && isNumber(arg["Id"]) && isString(arg["Email"]) && isNumber(arg["Role"])
}

function getSession(arg: SessionLikeArg) {

    function isShell(arg: IOmniaShell | IOmniaSession): arg is IOmniaShell {
        return isFunction(arg["getSession"])
    }

    return isShell(arg) ? arg.getSession() : arg

}

export function hasOwnerLikeRole(session: IOmniaSession) {

    if (isAdmin(session)) {
        return true
    }

    if (!session.config.holdingUserPermissions) {
        return false
    }

    if (session.currentHoldingUserRole === HoldingUserRole.Owner) {
        return true
    }

}

export function isHoldingReadOnly(session: IOmniaSession, holding?: IHolding) {
    return !canEditHolding(session, holding)
}

export function canEditHolding(session: IOmniaSession, holding?: IHolding) {

    if (!session.isReadOnly(p => p.holdings)) return true

    const isCurrent = (!holding) || (holding.HoldingId === session.holdingId)

    return isCurrent
        ? session.permissions.currentHolding.write
        : false
}

